import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Grid } from '@mantine/core';
import { ExtractionResults, ImageViewer, UploadSection } from '@features/index';
import { useExtraction, useDoctypes } from '@hooks/useContext';
import { useBrowserNotifications } from '@src/hooks/useBrowserNotifications';
import { Toast } from '@src/components';
import type { ExtractionOptions } from '@src/hooks/extraction/useExtraction';
import './ExtractionPage.scss';

const ExtractionPage: React.FC = () => {
    const { doctypeCode: doctypeCodeParam } = useParams<{ doctypeCode: string }>();
    const { requestPermission } = useBrowserNotifications();
    const {
        toast,
        hideToast,
        doctypeCode,
        setDoctypeCode,
        resetState,
        processResultsInfo,
    } = useExtraction();

    const {
        fetchDoctypes,
    } = useDoctypes();

    const [uploadedFile, setUploadedFile] = useState<File>();
    const [extractionOptions, setExtractionOptions] = useState<ExtractionOptions>({});
    const lastProcessIdRef = useRef<string>('');

    useEffect(() => {
        setUploadedFile(undefined);
        setExtractionOptions({});
    }, [doctypeCode]);

    useEffect(() => {
        requestPermission();
    }, [requestPermission]);

    useEffect(() => {
        fetchDoctypes();
    }, [fetchDoctypes]);

    // Set doctypeCode from URL param
    useEffect(() => {
        if (doctypeCodeParam) {
            setDoctypeCode(doctypeCodeParam);
        }
    }, [doctypeCodeParam, setDoctypeCode]);

    // Cleanup on unmount only
    useEffect(() => {
        return () => {
            resetState();
            setDoctypeCode('');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run cleanup only on unmount, not when resetState changes

    useEffect(() => {
        if (processResultsInfo.length === 0) return;

        const latestProcess = processResultsInfo[0];

        if (latestProcess.id !== lastProcessIdRef.current) {
            const isCompleted =
                latestProcess.status === 'COMPLETED' ||
                latestProcess.status === 'PARTIAL_COMPLETED' ||
                latestProcess.status === 'FAILED';

            if (isCompleted) {
                lastProcessIdRef.current = latestProcess.id;
                setUploadedFile(undefined);
                setExtractionOptions({});
            }
        }
    }, [processResultsInfo]);

    return (
        <Grid className="extraction-page">
            <Grid.Col span={{ base: 12, md: 2 }}>
                <UploadSection
                    uploadedFile={uploadedFile}
                    extractionOptions={extractionOptions}
                    onFileUpload={setUploadedFile}
                    onOptionsChange={setExtractionOptions}
                />
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 5 }}>
                <ImageViewer
                    uploadedFile={uploadedFile}
                    extractionOptions={extractionOptions}
                    onFileUpload={setUploadedFile}
                    onOptionsChange={setExtractionOptions}
                />
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 5 }}>
                <ExtractionResults />
            </Grid.Col>
            <Toast toast={toast} onClose={hideToast} />
        </Grid>
    );
};

export default ExtractionPage;
